import { useCallback, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';

import {
  LocaleContext,
  readStoredLocale,
  setLocaleForHttp,
  storeLocale,
  tForLocale,
  type Locale,
} from './locale';

/*
 * Applies the chosen locale: sets `document.documentElement.lang` for a11y,
 * keeps the module-level getLocale() in sync for non-React readers (the HTTP
 * client's Accept-Language header, toasts fired from the api layer), and
 * provides `t(key, params)` over the strings catalogs. The default comes from
 * navigator.language until the user picks a language in Profile settings.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);

  useLayoutEffect(() => {
    document.documentElement.lang = locale;
    setLocaleForHttp(locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    storeLocale(next);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => tForLocale(locale, key, params),
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
